// DigestBanner — shows today's overdue + due-this-week scoped to the current user.
// Internal users in portal view: digest is scoped to that client_id.
// Click "Send to my inbox" to write the digest to email_outbox.
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Clock, Send, ChevronDown, ChevronUp, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useView } from "@/context/ViewContext";

export default function DigestBanner({ personal = false, compact = false }) {
    const { portalClientId, isInternalView } = useView();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);

    const params = personal
        ? { personal: true }
        : (portalClientId && !isInternalView) ? { client_id: portalClientId } : {};

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/digest/me", { params });
            setData(data);
        } catch {
            setData(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portalClientId, isInternalView, personal]);

    useEffect(() => { load(); }, [load]);

    if (!data) return null;
    const { summary, overdue, due_week, scope_label } = data;
    const total = summary.overdue + summary.due_week;
    const allClear = total === 0;

    async function send() {
        setSending(true);
        try {
            const { data } = await api.post("/digest/me/send", null, { params });
            toast.success("Digest sent to your inbox", {
                description: `Subject: ${data.subject}`,
                icon: <CheckCircle2 className="w-4 h-4 text-[#3F7D3A]" />,
            });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not send digest");
        } finally {
            setSending(false);
        }
    }

    return (
        <div data-testid={personal ? "digest-banner-personal" : "digest-banner"} className="rounded-[11px] border overflow-hidden"
             style={{
                 background: allClear ? "#FFFFFF" : "#FFF4EE",
                 borderColor: allClear ? "#D2E5CC" : "#F0C9B7",
             }}>
            <div role="button" tabIndex={0}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); } }}
                data-testid={personal ? "digest-banner-personal-toggle" : "digest-banner-toggle"}
                className={`w-full flex items-center gap-4 text-left cursor-pointer hover:bg-[#FCFBF9] transition-colors outline-none focus:ring-2 focus:ring-[var(--brand-accent,_#C75A1B)]/30 ${compact ? "px-4 py-2.5" : "px-5 py-3.5"}`}>
                {allClear ? (
                    <span className={`rounded-[9px] grid place-items-center ${compact ? "w-7 h-7" : "w-9 h-9"}`}
                          style={{ background: "#E8F1E6", color: "#3F7D3A" }}>
                        <CheckCircle2 className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
                    </span>
                ) : (
                    <span className={`rounded-[9px] grid place-items-center ${compact ? "w-7 h-7" : "w-9 h-9"}`}
                          style={{ background: summary.overdue ? "#FBE8E5" : "#FCF1DC",
                                   color: summary.overdue ? "#B33A2E" : "#C68A1C" }}>
                        <AlertTriangle className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
                    </span>
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[.1em] font-bold" style={{ color: "#9A968F" }}>
                        Today’s digest · {scope_label}
                    </div>
                    <div className={`font-semibold text-[#1F1F1F] mt-0.5 ${compact ? "text-[12.5px]" : "text-[13.5px]"}`}>
                        {allClear ? (
                            <>You’re all clear — nothing overdue, nothing due this week.</>
                        ) : (
                            <>
                                You have{" "}
                                {summary.overdue > 0 && (
                                    <b style={{ color: "#B33A2E" }}>{summary.overdue} overdue</b>
                                )}
                                {summary.overdue > 0 && summary.due_week > 0 && " and "}
                                {summary.due_week > 0 && (
                                    <b style={{ color: "#C68A1C" }}>{summary.due_week} due this week</b>
                                )}
                                .
                            </>
                        )}
                    </div>
                </div>
                {!allClear && !compact && (
                    <button
                        type="button"
                        data-testid="digest-banner-send"
                        onClick={(e) => { e.stopPropagation(); send(); }}
                        onKeyDown={(e) => e.stopPropagation()}
                        disabled={sending}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[11.5px] font-semibold text-white disabled:opacity-70 transition-colors"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#9F4212")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "var(--brand-accent, #C75A1B)")}
                    >
                        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Send to my inbox
                    </button>
                )}
                <span className="w-7 h-7 rounded-[6px] grid place-items-center" style={{ color: "#6F6A64" }}>
                    {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </span>
            </div>

            {open && !allClear && (
                <div className="border-t" style={{ borderColor: "#F0EDE8" }}>
                    {summary.overdue > 0 && (
                        <ItemList title="Overdue" tone="#B33A2E" toneBg="#FBE8E5" items={overdue} icon={AlertTriangle} />
                    )}
                    {summary.due_week > 0 && (
                        <ItemList title="Due this week" tone="#C68A1C" toneBg="#FCF1DC" items={due_week} icon={Clock} />
                    )}
                </div>
            )}
        </div>
    );
}

function ItemList({ title, tone, toneBg, items, icon: Icon }) {
    return (
        <div>
            <div className="px-5 py-2 text-[11px] font-bold uppercase tracking-wider flex items-center gap-2"
                 style={{ background: toneBg, color: tone }}>
                <Icon className="w-3.5 h-3.5" /> {title} · {items.length}
            </div>
            <ul>
                {items.map((a) => (
                    <li key={a.id} data-testid={`digest-item-${a.id}`}
                        className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0 hover:bg-[#FCFBF9]"
                        style={{ borderColor: "#F0EDE8" }}>
                        {a.client_colour && (
                            <span className="w-6 h-6 rounded-[5px] grid place-items-center text-[9px] font-bold text-white flex-shrink-0"
                                  style={{ background: a.client_colour }}>
                                {a.client_initials}
                            </span>
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{a.title}</div>
                            <div className="text-[10.5px]" style={{ color: "#6F6A64" }}>
                                {a.client_name}{a.building_name ? ` · ${a.building_name}` : ""}
                                {a.assignee_name ? ` · ${a.assignee_name}` : ""}
                            </div>
                        </div>
                        <span className="text-[11.5px] font-mono font-semibold flex-shrink-0"
                              style={{ color: tone }}>
                            {a.due_date}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
